import {
  Component,
  Input,
  Output,
  EventEmitter,
  LOCALE_ID,
  Inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SignAtpDialogComponent } from '../sign-atp-dialog/sign-atp-dialog.component';
import { AllbidsDialogComponent } from '../allbids-dialog/allbids-dialog.component';
import { SiteService } from '../services/site.service';
import { getStoredUser } from '../helpers/user-storage.util';
import { ToastrService } from 'ngx-toastr';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ProjectService } from '../services/project.service';
import { ModalService } from '../services/modal.service';
import { ProjectSpecialRequestDialogComponent } from '../project-special-request-dialog/project-special-request-dialog.component';
import { RouterLink } from '@angular/router';
import { AcceptOfferedProposalDialogComponent } from '../accept-offered-proposal-dialog/accept-offered-proposal-dialog.component';

@Component({
  standalone: true,
  selector: 'info-header',
  imports: [
    CommonModule,
    SignAtpDialogComponent,
    AllbidsDialogComponent,
    ProjectSpecialRequestDialogComponent,
    RouterLink,
    AcceptOfferedProposalDialogComponent,
  ],
  templateUrl: './info-header.component.html',
  styleUrl: './info-header.component.scss',
})
export class InfoHeader {
  @Input() expanded: boolean = false;
  @Input() data: any = '';
  @Input() type: string = '';
  @Input() id: number = 0;
  @Input() status: string = '';
  @Input() site: number = 0;
  @Input() address: any = { streetAddress: '', cityStateZip: '' };
  @Input() acreage: number = 0;
  @Input() landUse: string = '';
  @Input() tableA: string = '';
  @Input() item20OrItem21: string = '';
  @Input() dueDate: string = '';
  @Input() documents: any = '';
  @Input() turnTime: number = 0;
  @Input() bidAmount: number = 0;
  @Input() reviewStatus: string = '';
  @Input() atpDocumentPath: SafeResourceUrl = '';
  @Input() showStatus: boolean = true;
  @Input() showSite: boolean = true;
  @Input() showAddress: boolean = true;
  @Input() showAcreage: boolean = true;
  @Input() showLandUse: boolean = true;
  @Input() showSiteType: boolean = true;
  @Input() showDueDate: boolean = true;
  @Input() showActions: boolean = true;
  @Input() numberOfBids: number = 0;

  @Output() toggled = new EventEmitter<boolean>();

  openState: boolean = this.expanded;
  serviceInfoToggle: boolean = false;
  signAtpActive: boolean = false;
  allBidsActive: boolean = false;
  acceptAllOffersActive: boolean = false;
  editToggle: boolean = false;
  showAtp: boolean = true;
  showSubmitBid: boolean = true;
  showAccept: boolean = true;
  atpDocument: any;
  childModalHeaderTitle: string = 'ATP Signed & Project Active';
  childModalBody: string =
    'You have signed the ATP and the project can be viewed from the Active tab.';
  user = getStoredUser();
  enableSubmitAllBids: boolean = false;
  specialRequestToggle: boolean = false;
  invitationCreatedDate: any = '';
  invitationUpdatedDate: any = '';
  projectActiveDate: any = '';
  siteVisitRequired: boolean = false;
  isRush: boolean = false;

  constructor(
    private siteService: SiteService,
    private projectService: ProjectService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
    private modalService: ModalService,
    @Inject(LOCALE_ID) public locale: string
  ) {}

  ngOnInit() {
    this.openState = this.expanded;
    this.showAtp = this.data?.sites?.some(
      (site: { proposal: { status: string } }) =>
        site.proposal?.status === 'BID_WON'
    );
    this.showAccept = this.data?.sites?.some(
      (site: { proposal: { status: string } }) =>
        site.proposal?.status === 'OFFERED'
    );
    this.showSubmitBid = this.data?.sites?.some(
      (site: { proposal: { status: string } }) =>
        site.proposal?.status === 'INVITATION'
    );
    this.enableSubmitAllBids = this.numberOfBids = this.data.sites.filter(
      (siteDetail: any) =>
        siteDetail.proposal.bid > 0 &&
        (siteDetail.proposal.proposedTurnTime > 0 ||
          siteDetail.site.desiredByDate)
    ).length;

    // set the initial received date to the project creation date
    this.invitationCreatedDate = this.data.projectCreatedOn;
    // set the initial update date list to the project updated date
    let proposalCreatedUpdatedDates: Date[] = [this.data.projectUpdatedOn];
    // loop through all proposal and get the created on and updated on dates
    const proposalCreatedOnDates: Date[] = this.data.sites
      .filter((siteDetail: any) => siteDetail.proposal.createdOn)
      .map((siteDetail: any) => siteDetail.proposal.createdOn);
    if (proposalCreatedOnDates?.length > 0) {
      proposalCreatedUpdatedDates = proposalCreatedUpdatedDates.concat(
        proposalCreatedOnDates
      );
    }
    const proposalUpdatedOnDates: Date[] = this.data.sites
      .filter((siteDetail: any) => siteDetail.proposal.updatedOn)
      .map((siteDetail: any) => siteDetail.proposal.updatedOn);
    if (proposalUpdatedOnDates?.length > 0) {
      proposalCreatedUpdatedDates = proposalCreatedUpdatedDates.concat(
        proposalUpdatedOnDates
      );
    }
    const siteUpdatesOnDates: Date[] = this.data.sites
      .filter((siteDetail: any) => siteDetail.site.updatedOn)
      .map((siteDetail: any) => siteDetail.site.updatedOn);
    if (siteUpdatesOnDates?.length > 0) {
      proposalCreatedUpdatedDates =
        proposalCreatedUpdatedDates.concat(siteUpdatesOnDates);
    }
    if (proposalCreatedUpdatedDates.length > 0) {
      // sort the dates
      proposalCreatedUpdatedDates.sort(
        (a, b) => new Date(b).getTime() - new Date(a).getTime()
      );
      // set the invitation received and updated dates
      this.invitationUpdatedDate = proposalCreatedUpdatedDates[0];
      this.invitationCreatedDate =
        proposalCreatedUpdatedDates[proposalCreatedUpdatedDates.length - 1];
    }
    // find and sort the signed atp dates
    const signedAtpDateDates: Date[] = this.data.sites
      .filter((siteDetail: any) => siteDetail.proposal.atpSignedDate)
      .map((siteDetail: any) => siteDetail.proposal.atpSignedDate);
    if (signedAtpDateDates?.length > 0) {
      signedAtpDateDates.sort(
        (a, b) => new Date(a).getTime() - new Date(b).getTime()
      );
      this.projectActiveDate = signedAtpDateDates[0];
    }
    this.siteVisitRequired = this.data.sites.some(
      (siteDetail: any) =>
        siteDetail.site.siteVisitRequired &&
        (siteDetail.proposal.status !== 'BID_LOST' ||
          siteDetail.proposal.status !== 'NO_RESPONSE')
    );
    this.isRush = this.data.rush;
  }

  accordion() {
    this.openState = !this.openState;
    this.toggled.emit(this.openState);
  }

  toggleSubmitAllBidsModal() {
    this.allBidsActive = !this.allBidsActive;
  }

  toggleAtpAcceptedModal() {
    this.modalService.toggleOpenSignedAtpModal(
      this.data.projectId,
      this.siteVisitRequired
    );
  }

  toggleSpecialRequestModal() {
    this.specialRequestToggle = !this.specialRequestToggle;
  }

  toggleATPModal() {
    this.signAtpActive = !this.signAtpActive;
  }

  toggleAcceptAllOfferedProposalModal() {
    this.acceptAllOffersActive = !this.acceptAllOffersActive;
  }

  generateATP() {
    this.toastr.info(
      'Please wait a moment while we generate your ATP document. This process may take a few seconds.'
    );
    let atpDocumentResponse: any = null;
    this.siteService
      .generateAtp(this.data, this.user.id ?? '')
      .subscribe((response) => {
        if (response) {
          atpDocumentResponse = response;
          if (
            atpDocumentResponse.status === 'SUCCESS' &&
            atpDocumentResponse.data
          ) {
            this.atpDocument = atpDocumentResponse.data;
            if (this.atpDocument.documentPath) {
              this.atpDocumentPath =
                this.sanitizer.bypassSecurityTrustResourceUrl(
                  this.atpDocument.documentPath
                );
              this.toggleATPModal();
            } else {
              this.toastr.error(
                'There was an issue with genereating your ATP document. Please try again.'
              );
            }
          } else {
            this.toastr.error(atpDocumentResponse.message);
          }
        } else {
          this.toastr.error(
            'There was an issue with genereating your ATP document. Please try again.'
          );
        }
      });
  }

  signATP(event: { fullName: string }) {
    this.toastr.info('Accepting your signed ATP...');
    this.siteService
      .signAtp(
        {
          projectDetail: this.data,
          atpDocument: this.atpDocument,
          atpSignedBy: event.fullName,
        },
        this.user.id ?? ''
      )
      .subscribe((response) => {
        const updatedProjectDetail = response.data;
        this.projectService.updateProjectDataSubjectWithProjectDetail(
          updatedProjectDetail
        );
        this.toggleAtpAcceptedModal();
      });
  }

  declineATP(event: {
    declineReason: string;
    declineOtherReason: string;
    declineComments: string;
  }) {
    const updatedSiteDetails = this.data.sites.map((siteDetail: any) => {
      const updatedProposal = {
        ...siteDetail.proposal,
        declineReason: event.declineReason,
        declineOtherReason: event.declineOtherReason,
        declineComments: event.declineComments,
      };
      return { ...siteDetail, proposal: updatedProposal };
    });
    this.siteService
      .cancel(this.user.id ?? '', { ...this.data, sites: updatedSiteDetails })
      .subscribe((response: any) => {
        const apiResponse = response;
        if (apiResponse.status === 'FAILURE') {
          if (apiResponse.message) {
            this.toastr.error(apiResponse.message);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        } else {
          const updatedProjectDetail = response.data;
          if (updatedProjectDetail) {
            this.projectService.updateProjectDataSubjectWithProjectDetail(
              updatedProjectDetail
            );
            this.modalService.toggleOpenDeclineSuccessModal({}, false, true);
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        }
      });
  }
}
